import { Injectable, OnDestroy } from '@angular/core'
import { LanguageService, RoutingService, UserIdService } from '@spartacus/core'
import { UserAccountFacade } from '@spartacus/user/account/root'
import { BehaviorSubject, combineLatest, Observable, Subscription } from 'rxjs'
import { map, switchMap, take, tap } from 'rxjs/operators'
import { EbAuthStatePersistenceService } from '../../../auth/services/eb-auth-state-persistence.service'
import { EbAuthService } from '../../../auth/services/eb-auth.service'
import { EbOrderEntry } from '../../../cart/interface/eb-order-entry.interface'
import { EbFavorites } from '../../interfaces/eb-favorites.interface'
import { EbFavoritesAdapter } from '../connectors/eb-favorites.adapter'

@Injectable({
  providedIn: 'root'
})
export class EbFavoritesService implements OnDestroy {
  private listLoadedWith: string
  readonly details$ = new BehaviorSubject<EbFavorites>(undefined)
  readonly lists$ = new BehaviorSubject<EbFavorites[]>([])
  readonly listLoading$ = new BehaviorSubject<boolean>(false)
  private readonly subscriptions = new Subscription()

  constructor(
    protected favoritesAdapter: EbFavoritesAdapter,
    protected userIdService: UserIdService,
    protected languageService: LanguageService,
    private readonly routingService: RoutingService,
    protected userAccountService: UserAccountFacade,
    protected authService: EbAuthService,
    protected authStateService: EbAuthStatePersistenceService
  ) {}

  fetchFavoriteListDetails(listId: string): void {
    combineLatest([this.authService.isUserLoggedIn(), this.userIdService.takeUserId()])
      .pipe(
        take(1),
        switchMap(([loggedIn, userId]) => {
          if (loggedIn) {
            return this.favoritesAdapter.getDetails(userId, listId)
          } else {
            this.authStateService.internalLogout(false, '/')
          }
        })
      )
      .subscribe(updatedList => {
        this.details$.next(updatedList)
      })
  }

  getList() {
    return this.details$
  }

  getLists(productCode: string = null) {
    if (!this.listLoadedWith || this.listLoadedWith !== productCode) {
      this.loadLists(productCode)
    }
    return this.lists$
  }

  getGroupedLists(productCode: string = null) {
    return combineLatest([this.getLists(productCode), this.languageService.getActive()]).pipe(
      map(([lists, locale]) => {
        const sorted = [...lists].sort((a, b) =>
          a.name.localeCompare(b.name, locale, { sensitivity: 'base' })
        )
        return {
          private: sorted.filter(favorites => !favorites.shared),
          shared: sorted.filter(favorites => favorites.shared)
        }
      })
    )
  }

  private loadLists(productCode: string = null): void {
    this.listLoadedWith = productCode
    this.userIdService
      .takeUserId()
      .pipe(
        take(1),
        tap(() => {
          this.listLoading$.next(true)
        }),
        switchMap(userId =>
          productCode
            ? this.favoritesAdapter.getListsForProduct(userId, productCode)
            : this.favoritesAdapter.getLists(userId)
        )
      )
      .subscribe(lists => {
        this.listLoading$.next(false)
        this.lists$.next(lists)
      })
  }

  isAuthorizedForList(currentUser: string): Observable<boolean> {
    return this.userAccountService.get().pipe(
      map(user => {
        return user?.uid === currentUser
      })
    )
  }

  createFavorites(name: string, productCode?: string): Observable<any> {
    return combineLatest([this.authService.isUserLoggedIn(), this.userIdService.takeUserId()]).pipe(
      take(1),
      switchMap(([loggedIn, userId]) => {
        if (loggedIn) {
          return this.favoritesAdapter.createFavorites(userId, productCode, name)
        } else {
          this.authStateService.internalLogout(false, '/')
        }
      }),
      tap(lists => this.lists$.next(lists))
    )
  }

  removeFavorites(favorites: EbFavorites): void {
    combineLatest([this.authService.isUserLoggedIn(), this.userIdService.takeUserId()])
      .pipe(
        take(1),
        switchMap(([loggedIn, userId]) => {
          if (loggedIn) {
            return this.favoritesAdapter.removeFavorites(userId, favorites)
          } else {
            this.authStateService.internalLogout(false, '/')
          }
        })
      )
      .subscribe(lists => {
        this.lists$.next(lists)
        const list = lists.find(value => value.name === 'Recommendations')
        this.routingService.go({
          cxRoute: 'favorites',
          params: {
            listId: list.code,
            shared: list.shared ? 'shared' : 'private'
          }
        })
      })
  }

  removeEntryFromFavorites(favorites: EbFavorites, entry: EbOrderEntry): void {
    combineLatest([this.authService.isUserLoggedIn(), this.userIdService.takeUserId()])
      .pipe(
        take(1),
        switchMap(([loggedIn, userId]) => {
          if (loggedIn) {
            return this.favoritesAdapter.removeEntryFromFavorites(userId, favorites, entry)
          } else {
            this.authStateService.internalLogout(false, '/')
          }
        })
      )
      .subscribe(details => this.details$.next(details))
  }

  removeSelectedEntriesFromFavorites(
    favorites: EbFavorites,
    entriesToRemove: EbOrderEntry[] = []
  ): void {
    if (entriesToRemove.length) {
      combineLatest([this.authService.isUserLoggedIn(), this.userIdService.takeUserId()])
        .pipe(
          take(1),
          switchMap(([loggedIn, userId]) => {
            if (loggedIn) {
              return this.favoritesAdapter.removeSelectedEntriesFromFavorites(
                userId,
                favorites,
                entriesToRemove
              )
            } else {
              this.authStateService.internalLogout(false, '/')
            }
          })
        )
        .subscribe(details => this.details$.next(details))
    }
  }

  removeProductFromFavorites(productCode: string, favorites: EbFavorites): void {
    combineLatest([this.authService.isUserLoggedIn(), this.userIdService.takeUserId()])
      .pipe(
        take(1),
        switchMap(([loggedIn, userId]) => {
          if (loggedIn) {
            return this.favoritesAdapter.removeProductFromFavorites(userId, favorites, productCode)
          } else {
            this.authStateService.internalLogout(false, '/')
          }
        })
      )
      .subscribe(lists => this.lists$.next(lists))
  }

  addProductToFavorites(productCode: string, favorites: EbFavorites): void {
    combineLatest([this.authService.isUserLoggedIn(), this.userIdService.takeUserId()])
      .pipe(
        take(1),
        switchMap(([loggedIn, userId]) => {
          if (loggedIn) {
            return this.favoritesAdapter.addProductToFavorites(userId, productCode, favorites)
          } else {
            this.authStateService.internalLogout(false, '/')
          }
        })
      )
      .subscribe(lists => this.lists$.next(lists))
  }

  takeFavoritesByName(name: string): Observable<EbFavorites> {
    return this.lists$.pipe(
      take(1),
      map(lists => {
        return (
          lists.find(favorites => favorites.name === name) ??
          lists.find(favorites => favorites.name.toLocaleLowerCase() === name.toLocaleLowerCase())
        )
      })
    )
  }

  toggleSharedStatusOfList(favorites: EbFavorites, isShared: boolean): void {
    combineLatest([this.authService.isUserLoggedIn(), this.userIdService.takeUserId()])
      .pipe(
        switchMap(([loggedIn, userId]) => {
          if (loggedIn) {
            return this.favoritesAdapter.toggleSharedStatusOfFavorites(userId, favorites, isShared)
          } else {
            this.authStateService.internalLogout(false, '/')
          }
        })
      )
      .subscribe(updatedList => {
        this.details$.next(updatedList)
        this.getLists()
      })
  }

  updateNameOfFavorites(favorites: EbFavorites, listName: string): void {
    combineLatest([this.authService.isUserLoggedIn(), this.userIdService.takeUserId()])
      .pipe(
        take(1),
        switchMap(([loggedIn, userId]) => {
          if (loggedIn) {
            return this.favoritesAdapter.updateFavoritesName(userId, favorites, listName)
          } else {
            this.authStateService.internalLogout(false, '/')
          }
        })
      )
      .subscribe(details => {
        this.details$.next(details)
        this.getLists()
      })
  }

  updateCommentOnEntry(item: { entryNumber?: number; comment?: string }, list: EbFavorites) {
    combineLatest([this.authService.isUserLoggedIn(), this.userIdService.takeUserId()])
      .pipe(
        take(1),
        switchMap(([loggedIn, userId]) => {
          if (loggedIn) {
            return this.favoritesAdapter.updateCommentOnEntry(item, list, userId)
          } else {
            this.authStateService.internalLogout(false, '/')
          }
        })
      )
      .subscribe(details => {
        this.details$.next(details)
        this.getLists()
      })
  }

  ngOnDestroy() {
    this.subscriptions.unsubscribe()
  }
}
